/**
 * Hide-on-mark for recap surfaces (recap/pill redesign spec §2–§4).
 *
 * Recap cards have no ✕ — a card hides ONLY after the viewer MARKS at least
 * one person in the We Met roster for that event (opening the roster alone
 * never hides; the 48h window is the surfaces' own ceiling regardless).
 * Hidden comes from two sources, either sufficient:
 *
 * 1. PERSISTED — `onMarked()` (wired to `WeMetRosterDrawer`'s `onMarked`)
 *    writes a per-viewer, per-event, per-surface record under
 *    `recap-mark-hide:{userId}:{eventId}:{scope}`. The record says what THIS
 *    viewer did, so it is keyed by user: on a shared device one account's mark
 *    must not hide the recap for the next account. Reads fail toward shown,
 *    writes are best-effort (in-memory state still hides the current view when
 *    storage is denied). With no resolved user (loading / guest) nothing is
 *    read or persisted — `onMarked` still hides the current mount in memory.
 * 2. DERIVED — when the We Met connections data is already at hand, ≥1 mark
 *    BY the viewer at this event (pending_outgoing or confirmed) hides too,
 *    so an already-recapped event never re-shows across surfaces or devices.
 *    The cache read is PASSIVE (`enabled: false`): recap surfaces never fire
 *    the auth-only connections request; they react to whatever the drawers /
 *    connections tab have loaded — including the fanout's optimistic insert
 *    (and its rollback on a hard failure).
 *
 * Records never fall back to an unkeyed read (it would leak across accounts).
 * They have no TTL: other accounts' records and earlier key formats stay on
 * disk, inert; nothing sweeps them.
 *
 * `onMarked()` also signals `subscribeRecapMarks` listeners: a ROW-level filter
 * can only do pure reads, so it subscribes to re-run when a mark lands and the
 * row and its card flip together instead of stranding an empty SwipeRow slot.
 *
 * `deferWhile` holds a hidden FLIP back (the write still lands) while the
 * roster drawer is open: the drawer mounts inside the card's subtree, so
 * hiding mid-flow would unmount an open drawer. Pass the drawer's open state;
 * on close the surface hides on its next render.
 */
import { useState } from 'react';
import { useCurrentUser } from '@hooks/useCurrentUser';
import { useWeMetConnectionsList } from '@hooks/useWeMetConnectionsV2';
import { deriveWeMetUIState, type WeMetConnectionRead } from '@models/api/we-met-connection';
import { defineStoredValue, FLAG } from '@utils/storedValue';

/**
 * The recap surfaces that hide on mark, each with its own persisted record.
 * Cross-surface consistency rides on the derived source — a mark is
 * event-level truth.
 */
export type RecapMarkScope = 'home-recap' | 'events-tab-recap';

// Compile-time exhaustiveness: a new scope member fails to type-check until
// listed here, so cleanup can never silently skip a scope key.
const SCOPE_KEYS: Record<RecapMarkScope, null> = {
  'home-recap': null,
  'events-tab-recap': null,
};
const ALL_SCOPES = Object.keys(SCOPE_KEYS) as readonly RecapMarkScope[];

// Keyed by `${userId}:${eventId}:${scope}` — the primitive's single-id key form.
const markHide = defineStoredValue({
  storage: 'local',
  key: (id: string) => `recap-mark-hide:${id}`,
  ...FLAG,
});

const idFor = (userId: string, eventId: string, scope: RecapMarkScope) =>
  `${userId}:${eventId}:${scope}`;

// ── Change signal ────────────────────────────────────────────────────────────
// A version counter is the `useSyncExternalStore` snapshot: primitive, stable
// between marks, bumped by every landed mark.

const markListeners = new Set<() => void>();
let marksVersion = 0;

function notifyMarked(): void {
  marksVersion += 1;
  for (const listener of markListeners) {
    listener();
  }
}

/** `useSyncExternalStore` subscribe for landed marks. */
export function subscribeRecapMarks(listener: () => void): () => void {
  markListeners.add(listener);
  return () => {
    markListeners.delete(listener);
  };
}

/** `useSyncExternalStore` snapshot: bumps on every landed mark. */
export function readRecapMarksVersion(): number {
  return marksVersion;
}

/**
 * Pure read of the PERSISTED portion — for a parent that must know whether a
 * child surface would render before mounting it. It cannot see the derived
 * source; a list-level parent combines it with `selectViewerHasEventMark` over
 * one connections read. No user, absent, malformed and unreadable storage all
 * fail toward shown.
 */
export function readRecapMarkHidden(
  userId: string | undefined,
  eventId: string,
  scope: RecapMarkScope,
): boolean {
  return !!userId && markHide.read(idFor(userId, eventId, scope)) === true;
}

/**
 * Drop every scope's record for an event — the cleanup hook for whoever knows
 * the event has left its recap window (the records are dead past the 48h
 * ceiling). Enumerates the closed scope set; no localStorage scan.
 */
export function removeRecapMarkHides(userId: string, eventId: string): void {
  for (const scope of ALL_SCOPES) markHide.clear(idFor(userId, eventId, scope));
}

/**
 * Pure selector: does the connections list show ≥1 mark BY the viewer at this
 * event? A viewer mark is a row they initiated (pending_outgoing) or a
 * confirmed connection (confirming an incoming request IS marking — the
 * roster drawer routes those selections to confirm). `pending_incoming` is
 * someone ELSE's mark; deleted rows never count. Exported so list-level
 * consumers (the Events-tab strip) can resolve many events from one read —
 * the `selectWeMetConnectionWith` precedent.
 */
export function selectViewerHasEventMark(
  connections: WeMetConnectionRead[] | undefined,
  eventId: string,
): boolean {
  if (!connections) {
    return false;
  }
  return connections.some(connection => {
    if (connection.event_id !== eventId) {
      return false;
    }
    const uiState = deriveWeMetUIState(connection.status, connection.is_initiator);
    return uiState === 'pending_outgoing' || uiState === 'confirmed';
  });
}

export function useRecapMarkHidden(
  eventId: string,
  scope: RecapMarkScope,
  options?: { deferWhile?: boolean },
): { hidden: boolean; onMarked: () => void } {
  const deferWhile = options?.deferWhile ?? false;
  // Primitive extracted so deps stay primitive (the InviteBanner idiom).
  const { user } = useCurrentUser();
  const userId = user?.id;
  // State identity: viewer + event + scope. Includes the no-user case so a
  // guest-mount's in-memory mark can't carry over to a different event.
  const stateKey = `${userId ?? ''}:${eventId}:${scope}`;
  // Seed from the persisted record (mirror included): a remount inside the
  // session still starts hidden.
  const [persisted, setPersisted] = useState(() => ({
    key: stateKey,
    marked: readRecapMarkHidden(userId, eventId, scope),
  }));

  // Guarded render-phase reset (React's "store info from previous renders"
  // pattern): a userId/eventId/scope change re-reads its own key without a
  // derived-state effect — this is also what re-derives when the user resolves
  // late or switches without a remount. Self-disables once the key catches up.
  if (persisted.key !== stateKey) {
    setPersisted({ key: stateKey, marked: readRecapMarkHidden(userId, eventId, scope) });
  }

  // Passive cache read: `enabled: false` subscribes to the cache entry
  // without ever firing the request, so mounting a recap surface costs no
  // network — derivation only sees marks already at hand.
  const { data: connections } = useWeMetConnectionsList({ enabled: false });
  // Key-fresh persisted read: the guarded reset above hasn't committed in THIS
  // render, so on a viewer/event/scope change we must not seed from the OLD
  // key's record (a stale value could stick behind deferWhile, and for a
  // viewer switch it would show A's mark to B for a render).
  const marked =
    persisted.key === stateKey ? persisted.marked : readRecapMarkHidden(userId, eventId, scope);
  const rawHidden = marked || selectViewerHasEventMark(connections, eventId);

  // Reported value, frozen while deferWhile holds (same guarded render-phase
  // pattern — each branch self-disables once state matches the inputs).
  const [reported, setReported] = useState(() => ({ key: stateKey, hidden: rawHidden }));
  if (reported.key !== stateKey) {
    setReported({ key: stateKey, hidden: rawHidden });
  } else if (!deferWhile && reported.hidden !== rawHidden) {
    setReported({ key: stateKey, hidden: rawHidden });
  }

  const onMarked = () => {
    // No resolved viewer: nothing is keyed or persisted; this mount hides in memory only.
    if (userId) {
      markHide.write(true, idFor(userId, eventId, scope));
      notifyMarked();
    }
    setPersisted({ key: stateKey, marked: true });
  };

  return { hidden: reported.hidden, onMarked };
}
